import React, { useEffect, useRef, useState } from "react";
import { motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from "framer-motion";
import Header from "../../components/header";
import Footer from "../../components/footer";
import customerImage from "../../images/savings-banner-image-one.jpg";
import coinsImage from "../../images/savings-banner-image-two.jpg";
import coupleImage from "../../images/saings-banner-image-three.jpg";
import basicIllustration from "../../images/saving-account/wallet-desktop.svg";
import targetIllustration from "../../images/saving-account/target-desktop.png";
import depositIllustration from "../../images/saving-account/deposit-desktop.png";
import basicIllustrationMobile from "../../images/saving-account/wallet-mobile.svg";
import targetIllustrationMobile from "../../images/saving-account/target-mobile.svg";
import depositIllustrationMobile from "../../images/saving-account/deposit-mobile.svg";
import footerLogo from "../../images/saving-account/footer-logo.png";
import styles from "./index.module.css";

const accountUrl = "https://zikoramfb.qoreonline.com/onboarding/type";
const appUrl = "https://onelink.to/zikora";

const options = [
  { title: "Basic Savings", description: "For everyday saving with easy access to your money whenever you need it.", desktopRate: "4%", mobileRate: "2%", note: "Interest paid monthly", image: basicIllustration, mobileImage: basicIllustrationMobile, imageAlt: "Illustration of a savings wallet" },
  { title: "Target Savings", description: "Save gradually toward a specific goal like rent, school fees, travel, or business plans.", desktopRate: "6%", mobileRate: "8%", note: "Auto-debit from any account", image: targetIllustration, mobileImage: targetIllustrationMobile, imageAlt: "Illustration of a savings target", dark: true },
  { title: "Fixed Deposit", mobileTitle: "Fixed Deposits", description: "Lock in funds for a set period and earn higher returns over time.", desktopRate: "18%", mobileRate: "14%", note: "90, 180 or 365-day terms", image: depositIllustration, mobileImage: depositIllustrationMobile, imageAlt: "Illustration of growing fixed deposits" },
];
const optionStops = [0, 0.4, 0.72, 1];
const sceneStops = [0, 0.36, 0.62, 1];

const questions = [
  { question: "How do I open a Zikora account?", answer: "Download the Zikora App, enter your details, complete your BVN verification, and start banking in just a few minutes." },
  { question: "Is Zikora Bank licensed?", answer: "Yes. Zikora Microfinance Bank is licensed and regulated by the Central Bank of Nigeria and insured by the Nigeria Deposit Insurance Corporation." },
  { question: "How can I apply for a loan?", answer: "Apply through the Zikora App or visit a Zikora branch to discuss the loan that fits your needs." },
  { question: "Can I transfer money using the app?", answer: "Yes. You can send money to Nigerian bank accounts through the Zikora App." },
  { question: "Where is Zikora located?", answer: "Our head office is in Abatete, Anambra State." },
];

const Label = ({ children, light = false }) => <div className={`${styles.label} ${light ? styles.labelLight : ""}`}><span aria-hidden="true" />{children}</div>;

const Actions = ({ dark = false }) => <div className={styles.actions}>
  <a className={styles.primaryButton} href={accountUrl} target="_blank" rel="noopener noreferrer">Open Account</a>
  <a className={`${styles.secondaryButton} ${dark ? styles.secondaryButtonDark : ""}`} href={appUrl} target="_blank" rel="noopener noreferrer">Download the App</a>
</div>;

const OptionCopy = ({ option }) => <div className={styles.optionCopy}>
  <div><h3><span className={styles.desktopTitle}>{option.title}</span><span className={styles.mobileTitle}>{option.mobileTitle || option.title}</span></h3><p>{option.description}</p></div>
  <div className={styles.rate}><strong><span className={styles.desktopRate}>{option.desktopRate}</span><span className={styles.mobileRate}>{option.mobileRate}</span></strong><span><b>Per Annum</b><small>{option.note}</small></span></div>
</div>;

const OptionArtContent = ({ option, focusable = true }) => <>
  <picture><source media="(max-width: 600px)" srcSet={option.mobileImage} /><img src={option.image} alt={option.imageAlt} loading="lazy" decoding="async" /></picture>
  <a href={accountUrl} target="_blank" rel="noopener noreferrer" tabIndex={focusable ? undefined : -1}>Open Account <span aria-hidden="true">→</span></a>
</>;

const artClass = (option) => `${styles.optionArt} ${option.dark ? styles.darkArt : ""} ${option.title === "Basic Savings" ? styles.basicArt : ""} ${option.title === "Target Savings" ? styles.targetArt : ""} ${option.title === "Fixed Deposit" ? styles.depositArt : ""}`;

const Money = ({ value }) => <>{new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(value)}</>;

const ProgressSegment = ({ progress, index, initialFill, reducedMotion, stops }) => {
  const fill = useTransform(progress, [stops[index], stops[index + 1]], [index === 0 ? initialFill : 0, 1]);
  return <span><motion.i style={{ scaleX: reducedMotion ? (index === 0 ? initialFill : 0) : fill }} /></span>;
};

const SavingsCalculator = () => {
  const [amount, setAmount] = useState(5000000);
  const [months, setMonths] = useState(12);
  const [mobileRate, setMobileRate] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 600px)").matches);
  useEffect(() => {
    const media = window.matchMedia("(max-width: 600px)");
    const updateRate = () => setMobileRate(media.matches);
    media.addEventListener("change", updateRate);
    return () => media.removeEventListener("change", updateRate);
  }, []);
  const interest = amount * (mobileRate ? 0.14 : 0.18) * months / 12;
  return <section className={styles.calculatorSection}>
    <div className={styles.calculatorInner}>
      <div className={styles.calculatorCopy}>
        <Label>Fixed Deposit Calculator</Label>
        <h2>See exactly what your money will earn.</h2>
        <p>Adjust the sliders to estimate your returns in real time using current fixed deposit rates.</p>
        <small>Final rates are confirmed at booking.</small>
      </div>
      <div className={styles.calculatorCard}>
        <div className={styles.calculatorField}>
          <label htmlFor="saving-amount">Amount</label><output htmlFor="saving-amount"><Money value={amount} /></output>
          <div className={styles.rangeLabels}><span>₦50,000</span><span>₦20,000,000</span></div>
          <input id="saving-amount" type="range" min="50000" max="20000000" step="50000" value={amount} onChange={(event) => setAmount(Number(event.target.value))} />
        </div>
        <div className={styles.calculatorField}>
          <label htmlFor="saving-tenor">Tenor</label><output htmlFor="saving-tenor">{months} months</output>
          <div className={styles.rangeLabels}><span>Min: 90 days</span><span>Max: 36 months</span></div>
          <input id="saving-tenor" type="range" min="3" max="36" step="1" value={months} onChange={(event) => setMonths(Number(event.target.value))} />
        </div>
        <div className={styles.calculatorResults} aria-live="polite">
          <div><span>Expected Interest</span><strong><Money value={interest} /></strong></div>
          <div><span>Total Payout at Maturity</span><strong><Money value={amount + interest} /></strong></div>
        </div>
      </div>
    </div>
  </section>;
};

const SavingsAccount = () => {
  const [openQuestion, setOpenQuestion] = useState(0);
  const [mobileLayout, setMobileLayout] = useState(() => typeof window !== "undefined" && window.matchMedia("(max-width: 600px)").matches);
  const [pinWholeScene, setPinWholeScene] = useState(() => typeof window !== "undefined" && window.matchMedia("(min-width: 901px) and (min-height: 820px)").matches);
  const [activeOption, setActiveOption] = useState(0);
  const optionsSectionRef = useRef(null);
  const optionsTrackRef = useRef(null);
  const reducedMotion = useReducedMotion();
  const { scrollYProgress: trackProgress } = useScroll({ target: optionsTrackRef, offset: ["start 25%", "end 75%"] });
  const { scrollYProgress: sectionProgress } = useScroll({ target: optionsSectionRef, offset: ["start 25%", "end 75%"] });
  const scrollYProgress = pinWholeScene ? sectionProgress : trackProgress;
  const stops = pinWholeScene ? sceneStops : optionStops;
  useMotionValueEvent(scrollYProgress, "change", (value) => {
    if (reducedMotion) return;
    const index = value < stops[1] ? 0 : value < stops[2] ? 1 : 2;
    setActiveOption((current) => index === current ? current : index);
  });
  useEffect(() => {
    const media = window.matchMedia("(max-width: 600px)");
    const updateLayout = () => setMobileLayout(media.matches);
    media.addEventListener("change", updateLayout);
    return () => media.removeEventListener("change", updateLayout);
  }, []);
  useEffect(() => {
    const media = window.matchMedia("(min-width: 901px) and (min-height: 820px)");
    const updatePinning = () => setPinWholeScene(media.matches);
    media.addEventListener("change", updatePinning);
    return () => media.removeEventListener("change", updatePinning);
  }, []);
  return <main className={styles.page}>
    <Header solid compactMobile />
    <section className={styles.hero}><div className={styles.heroInner}>
      <div className={styles.heroCopy}>
        <Label>Savings &amp; Fixed Deposit</Label>
        <h1>Save with confidence. <em>Grow</em> with patience.</h1>
        <p>Whether you're saving daily, planning toward a goal, or growing long-term funds, Zikora gives you flexible ways to build your future.</p>
        <Actions />
      </div>
      <div className={styles.heroImages}>
        <img className={styles.customerImage} src={customerImage} alt="Customer checking savings on the Zikora app" />
        <div className={styles.heroImageStack}><img src={coinsImage} alt="Stacks of coins representing savings growth" /><img src={coupleImage} alt="Couple planning their savings together" /></div>
      </div>
    </div></section>

    <section className={styles.optionsSection} ref={optionsSectionRef}><div className={styles.sectionShell}>
      <div className={styles.optionsHeading}><Label>Saving Accounts</Label><h2>Savings options built for different goals</h2><p>Choose an account that matches how you want to save, grow, or plan ahead.</p></div>
      <div className={`${styles.optionsTrack} ${reducedMotion ? styles.staticTrack : ""}`} ref={optionsTrackRef}><div className={styles.optionsStage}>
        <div className={styles.progress} aria-hidden="true">{options.map((option, index) => <ProgressSegment key={option.title} progress={scrollYProgress} index={index} initialFill={mobileLayout ? 0.36 : 0.65} reducedMotion={reducedMotion} stops={stops} />)}</div>
        <div className={`${styles.cardViewport} ${reducedMotion ? styles.staticCards : ""}`}>
          {reducedMotion ? options.map((option) => <article className={styles.optionRow} key={option.title}>
            <OptionCopy option={option} />
            <div className={artClass(option)}><OptionArtContent option={option} /></div>
          </article>) : <div className={styles.optionRow}>
            <div className={styles.copyViewport} aria-live="polite">
              {options.map((option, index) => <motion.div key={option.title} aria-hidden={index !== activeOption}
                initial={false} animate={{ opacity: index === activeOption ? 1 : 0, y: index === activeOption ? 0 : index < activeOption ? -10 : 10 }}
                transition={{ opacity: { duration: 0.38, ease: "easeOut" }, y: { type: "spring", stiffness: 150, damping: 28 } }}
                style={{ pointerEvents: index === activeOption ? "auto" : "none" }}>
                <OptionCopy option={option} />
              </motion.div>)}
            </div>
            <div className={styles.artDeck}>{options.map((option, index) => <motion.div key={option.title} className={artClass(option)} aria-hidden={index !== activeOption}
              initial={false} animate={{ opacity: index <= activeOption ? 1 : 0, y: index < activeOption ? -18 * (activeOption - index) : index > activeOption ? 72 : 0, scale: index < activeOption ? 1 - 0.035 * (activeOption - index) : index > activeOption ? 0.94 : 1 }}
              transition={{ opacity: { duration: 0.38, ease: "easeOut" }, y: { type: "spring", stiffness: 120, damping: 25, mass: 0.9 }, scale: { type: "spring", stiffness: 120, damping: 25, mass: 0.9 } }} style={{ zIndex: index === activeOption ? 3 : index < activeOption ? 1 : 2, pointerEvents: index === activeOption ? "auto" : "none" }}>
              <OptionArtContent option={option} focusable={index === activeOption} />
            </motion.div>)}</div>
          </div>}
        </div>
      </div></div>
    </div></section>

    <SavingsCalculator />

    <section className={styles.faqSection}><div className={styles.sectionShell}><div className={styles.faqGrid}>
      <div className={styles.faqIntro}><Label>FAQs</Label><h2>Frequently asked questions you need to know before getting started</h2><a className={styles.faqContactDesktop} href="/contact">Have more questions? Contact Us</a></div>
      <div className={styles.questionList}>{questions.map(({ question, answer }, index) => <div className={styles.question} key={question}>
        <button type="button" aria-expanded={openQuestion === index} onClick={() => setOpenQuestion(openQuestion === index ? -1 : index)}><span>{question}</span><span className={styles.chevron} aria-hidden="true">⌃</span></button>
        {openQuestion === index && <p>{answer}</p>}
      </div>)}<a className={styles.faqContactMobile} href="/contact">Have more questions? Contact Us</a></div>
    </div></div></section>

    <section className={styles.ctaSection}><div className={styles.ctaCard}><Label light>Get Started</Label><h2>Ready to get started with smarter banking?</h2><p>Open your account in minutes or download the Zikora App to bank anytime, anywhere.</p><Actions dark /></div></section>
    <div className={styles.footerWrap}><Footer about logo={footerLogo} /></div>
  </main>;
};

export default SavingsAccount;
